import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { AlertTriangle } from "lucide-react";

/**
 * PROMPT 16-DELETE-CONFIRM-DIALOG (2026-08-26)
 * Konfirmasi hapus project dgn 2 gate:
 *   1. User WAJIB mengetik nama project persis (case-sensitive) — mencegah salah klik.
 *   2. User WAJIB mengisi `reason` minimal 5 karakter — dipakai audit log.
 * Tombol Hapus disabled sampai kedua gate terpenuhi. `onConfirm(reason)` dipanggil
 * saat user konfirmasi; parent bertanggung jawab memanggil DELETE endpoint dgn reason.
 */
export default function DeleteProjectDialog({ open, onOpenChange, project, isCompleted, onConfirm }) {
  const [typedName, setTypedName] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const target = project?.project_name || project?.wo_number || "";
  useEffect(() => {
    if (open) { setTypedName(""); setReason(""); setBusy(false); }
  }, [open]);
  const canConfirm = typedName.trim() === target && reason.trim().length >= 5 && !busy;
  const submit = async () => {
    if (!canConfirm) return;
    setBusy(true);
    try { await onConfirm(reason.trim()); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg rounded-none" data-testid="delete-project-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-rose-700">
            <AlertTriangle size={18} />
            {isCompleted ? "Hapus Permanen Project Selesai" : "Batalkan Project"}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div className="border border-rose-200 bg-rose-50 p-3 text-xs text-rose-900">
            <div className="font-semibold uppercase tracking-wider text-[10px] mb-1">Tidak dapat dibatalkan</div>
            Semua data project di bawah ini akan {isCompleted ? "dihapus permanen" : "dibatalkan"}. Audit log akan mencatat siapa yg menghapus, kapan, dan alasannya.
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Nama Project</div>
            <div data-testid="delete-project-target-name" className="border border-slate-300 bg-slate-50 px-3 py-2 text-sm font-mono">{target}</div>
          </div>
          <div>
            <Label htmlFor="typed_name" className="overline text-slate-600">Ketik ulang nama project untuk konfirmasi</Label>
            <Input
              id="typed_name"
              data-testid="delete-project-typed-name"
              value={typedName}
              onChange={(e) => setTypedName(e.target.value)}
              placeholder={target}
              className="rounded-none mt-1"
              autoComplete="off"
            />
            {typedName && typedName.trim() !== target && (
              <p className="text-[10px] text-rose-600 mt-1">Nama tidak cocok. Salin persis dari kotak di atas.</p>
            )}
          </div>
          <div>
            <Label htmlFor="reason" className="overline text-slate-600">Alasan penghapusan <span className="text-rose-600">*</span></Label>
            <Textarea
              id="reason"
              data-testid="delete-project-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Contoh: Duplikat dari WO #12345 / dibatalkan client 26 Agustus"
              className="rounded-none mt-1 min-h-[80px]"
              maxLength={500}
            />
            <p className="text-[10px] text-slate-500 mt-1">Minimal 5 karakter. Alasan disimpan di audit log & tidak dapat diubah setelah dikirim.</p>
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            data-testid="delete-project-cancel"
            onClick={() => onOpenChange(false)}
            className="text-[10px] uppercase tracking-wider font-semibold text-slate-600 hover:text-slate-800 border border-slate-300 px-3 py-2"
          >Batal</button>
          <button
            type="button"
            data-testid="delete-project-confirm"
            onClick={submit}
            disabled={!canConfirm}
            className="text-[10px] uppercase tracking-wider font-semibold text-white bg-rose-600 hover:bg-rose-700 px-3 py-2 disabled:opacity-40 disabled:cursor-not-allowed"
          >{busy ? "Menghapus…" : (isCompleted ? "Hapus Permanen" : "Batalkan Project")}</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
